// How one gift READS, on either side of it (arc 101 slice 11, plan doc 05).
// Pure label helpers, shared by the donor's table row and the maintainer's card
// — two consumers, so the rules live once rather than drifting into two
// vocabularies for one row.

import { t } from '../i18n';
import type { DonationRow } from './donations';

/**
 * Live / awaiting payment / parked — THREE states, not two.
 *
 * `active: false` means different things and both sides of a gift have to tell
 * them apart: a row carrying a `revoked_iso` was PARKED (the donor detached it,
 * their subscription lapsed, or the maintainer declined it), while one without is
 * a `money` gift Stripe has not confirmed yet. Collapsing them would tell someone
 * mid-checkout that their gift was cancelled.
 */
export function donationStatusKey(row: DonationRow): string {
  if (row.active) return 'donations.status_live';
  return row.revoked_iso === null ? 'donations.status_pending' : 'donations.status_parked';
}

/**
 * The gift's resource, MASKED: an id fragment, never a credential. A donated key
 * is sealed on arrival and no surface ever holds its value, so the most either
 * side can name is which row it was.
 */
export function donationResourceLabel(row: DonationRow): string {
  if (row.kind === 'money') return t('donations.resource_money');
  const ref = donationResourceRef(row);
  return ref === undefined ? t('donations.resource_none') : ref.slice(0, 8);
}

/** The full resource id for a `title`, or undefined when the gift pins none. */
export function donationResourceRef(row: DonationRow): string | undefined {
  return row.runner_id ?? row.byok_key_id ?? undefined;
}

/**
 * The project a gift names, as words: the pinned `owner/repo`, else "General
 * pool" for a pool gift, else "Pinned project" for a project gift whose repo row
 * is gone (the projection omits the name). ONE rule for the visible cell and
 * the detach button's accessible name, which once disagreed on that last case.
 */
export function donationTargetLabel(row: DonationRow): string {
  if (row.target_kind === 'project' && row.target_repo_full_name !== undefined) {
    return row.target_repo_full_name;
  }
  return row.target_kind === 'pool' ? t('donations.target_pool') : t('donations.target_unnamed');
}
